import Button, { ButtonType } from "components/Elements/Buttonv2";
import Input from "components/Elements/Inputv2";
import searchIconImage from "./svg/search-icon.svg";
import threeDotsIconImage from "./svg/three-dots-icon.svg";
import emptyDataImage from "./svg/empty-data.svg";
import React, { Fragment, useEffect, useState } from "react";
import Table from "components/Tablev2";
import { format } from "date-fns";
import { Menu, Transition } from "@headlessui/react";
import ApiService from "services/api.service";
import Template from "types/Template";
import { useNavigate } from "react-router-dom";
import Pagination from "components/Pagination";
import { toast } from "react-toastify";
import TemplateImportModal from "./Modals/TemplateImportModal";
import CreateTemplateWizardModal from "./Modals/CreateTemplateWizardModal";
import SequenceTable from "./SequenceTable";
import Modal from "components/Elements/Modalv2";
import { useDebounce } from "react-use";
import {
  keepPreviousData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import sortAscChevronsImage from "./svg/sort-asc-chevrons.svg";
import sortDescChevronsImage from "./svg/sort-desc-chevrons.svg";
import sortNoneChevronsImage from "./svg/sort-none-chevrons.svg";

export interface TemplateRowData {
  id: number;
  name: string;
  type: string;
  lastUpdate: string;
}

export enum SortProperty {
  LAST_UPDATE = "updatedAt",
}

export enum SortType {
  ASC = "asc",
  DESC = "desc",
}

export interface SortOptions {
  sortBy: SortProperty;
  sortType: SortType;
}

type TopTab = "templates" | "sequences";

const TEMPLATES_ITEMS_PER_PAGE = 10;

export interface TemplatesQueryParams {
  page: number;
  search: string;
  sortBy: SortProperty;
  sortType: SortType;
}

export const DEFAULT_TEMPLATES_QUERY_PARAMS: TemplatesQueryParams = {
  page: 1,
  search: "",
  sortBy: SortProperty.LAST_UPDATE,
  sortType: SortType.DESC,
};

// Single source of truth for the templates list request — used by both the
// page and the app-entry prefetch so they resolve to the same cache entry.
export const buildTemplatesQuery = (params: TemplatesQueryParams) => ({
  queryKey: [
    "templates",
    {
      page: params.page,
      search: params.search,
      sortBy: params.sortBy,
      sortType: params.sortType,
    },
  ],
  queryFn: async () => {
    const {
      data: { data: results, totalPages },
    } = await ApiService.get<{ data: Template[]; totalPages: number }>({
      url: `/templates?take=${TEMPLATES_ITEMS_PER_PAGE}&skip=${
        (params.page - 1) * TEMPLATES_ITEMS_PER_PAGE
      }&search=${params.search}&orderBy=${params.sortBy}&orderType=${
        params.sortType
      }&type=email&type=sms&type=webhook&type=push&type=in-app&type=slack`,
    });

    const rows: TemplateRowData[] = results.map((template) => ({
      id: template.id,
      name: template.name,
      type: template.type,
      lastUpdate: template.updatedAt,
    }));

    return { rows, totalPages };
  },
});

export const prefetchTemplates = (queryClient: QueryClient) =>
  queryClient.prefetchQuery(
    buildTemplatesQuery(DEFAULT_TEMPLATES_QUERY_PARAMS)
  );

const TemplateTablev2 = () => {
  const navigate = useNavigate();

  const [isNameTemplateOpen, setIsNameTemplateOpen] = useState(false);
  const [isChooserOpen, setIsChooserOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TopTab>("templates");
  const [sequenceCreateTrigger, setSequenceCreateTrigger] = useState(0);

  const queryClient = useQueryClient();

  const [showSearch, setShowSearch] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortOptions, setSortOptions] = useState<SortOptions>({
    sortBy: SortProperty.LAST_UPDATE,
    sortType: SortType.DESC,
  });

  const [currentPage, setCurrentPage] = useState(1);

  // Debounce the search box into the value the query uses so typing doesn't
  // fire a request per keystroke.
  useDebounce(() => setDebouncedSearch(search), 500, [search]);

  const { data, isPending, isError, isSuccess } = useQuery({
    // Shared factory so the page and the app-entry prefetch hit the same key.
    ...buildTemplatesQuery({
      page: currentPage,
      search: debouncedSearch,
      sortBy: sortOptions.sortBy,
      sortType: sortOptions.sortType,
    }),
    // Keep the current rows on screen while the next request runs, so paging,
    // filtering, and returning to the page never blank the table.
    placeholderData: keepPreviousData,
  });

  const rows = data?.rows ?? [];
  const pagesCount = data?.totalPages ?? 1;

  useEffect(() => {
    if (isError) toast.error("Failed to load data");
  }, [isError]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortOptions, debouncedSearch]);

  useEffect(() => {
    setSearch("");
  }, [showSearch]);

  // Other parts of the app dispatch this when they change templates.
  useEffect(() => {
    const onChange = () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    };
    window.addEventListener("leadstats:templates-changed", onChange);
    return () =>
      window.removeEventListener("leadstats:templates-changed", onChange);
  }, [queryClient]);

  type TemplatesCache = { rows: TemplateRowData[]; totalPages: number };

  // Duplicate a template and insert the returned copy at the top of the current
  // page (newest first under the default sort) instead of refetching.
  const duplicateMutation = useMutation({
    mutationFn: (id: string) =>
      ApiService.post<Template>({ url: `/templates/${id}/duplicate` }),
    onSuccess: ({ data: tmpl }) => {
      const row: TemplateRowData = {
        id: tmpl.id,
        name: tmpl.name,
        type: tmpl.type,
        lastUpdate: tmpl.updatedAt,
      };
      const { queryKey } = buildTemplatesQuery({
        page: currentPage,
        search: debouncedSearch,
        sortBy: sortOptions.sortBy,
        sortType: sortOptions.sortType,
      });
      queryClient.setQueryData<TemplatesCache>(queryKey, (old) =>
        old
          ? {
              ...old,
              rows: [row, ...old.rows].slice(0, TEMPLATES_ITEMS_PER_PAGE),
            }
          : old
      );
    },
  });

  const handleDuplicateTemplate = (id: string) =>
    toast.promise(duplicateMutation.mutateAsync(id), {
      pending: "Duplicating template...",
      success: "Template duplicated",
      error: "Failed to duplicate template",
    });

  // Delete a template optimistically: drop the row from every cached page, roll
  // back on failure, no list refetch.
  const deleteMutation = useMutation({
    mutationFn: (id: number) => ApiService.delete({ url: `/templates/${id}` }),
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: ["templates"] });
      const snapshot = queryClient.getQueriesData<TemplatesCache>({
        queryKey: ["templates"],
      });
      queryClient.setQueriesData<TemplatesCache>(
        { queryKey: ["templates"] },
        (old) =>
          old ? { ...old, rows: old.rows.filter((r) => r.id !== id) } : old
      );
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      context?.snapshot.forEach(([key, value]) =>
        queryClient.setQueryData(key, value)
      );
      toast.error("Failed to delete template");
    },
  });

  const handleDeleteTemplate = (id: number) => deleteMutation.mutate(id);

  return (
    <div className="p-5 flex flex-col gap-5 font-inter font-normal text-[14px] text-themed-text leading-[22px]">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-6">
          <div className="text-[20px] font-semibold leading-[28px]">
            Templates
          </div>
          <div className="flex border border-themed-border rounded overflow-hidden">
            <button
              className={`px-3 py-1 text-[13px] ${
                activeTab === "templates"
                  ? "bg-[#6366F1] text-white"
                  : "bg-themed-card text-themed-text"
              }`}
              onClick={() => setActiveTab("templates")}
            >
              Templates
            </button>
            <button
              className={`px-3 py-1 text-[13px] border-l border-themed-border ${
                activeTab === "sequences"
                  ? "bg-[#6366F1] text-white"
                  : "bg-themed-card text-themed-text"
              }`}
              onClick={() => setActiveTab("sequences")}
            >
              Sequences
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type={ButtonType.SECONDARY}
            onClick={() => setIsImportOpen(true)}
            id="import-template-button"
          >
            Import
          </Button>
          <Button
            type={ButtonType.PRIMARY}
            onClick={() => setIsChooserOpen(true)}
            id="create-template-button"
          >
            Create template
          </Button>
        </div>
      </div>

      {activeTab === "sequences" && (
        <SequenceTable
          createTrigger={sequenceCreateTrigger}
          onRequestCreateTemplate={() => {
            setActiveTab("templates");
            setIsNameTemplateOpen(true);
          }}
        />
      )}
      {activeTab === "templates" && (
        <div className="p-5 bg-themed-card rounded-lg flex flex-col gap-5">
          {rows.length === 0 && search === "" && isSuccess ? (
            <div className="w-full h-[300px] flex items-center justify-center select-none">
              <div className="flex flex-col items-center gap-5">
                <img src={emptyDataImage} />

                <div className="font-inter text-[16px] font-semibold leading-[24px] text-themed-textSecondary">
                  Create a template for your messages
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-end items-center">
                {showSearch ? (
                  <div className="flex gap-[10px] items-center">
                    <Input
                      value={search}
                      onChange={setSearch}
                      placeholder="Search all templates"
                      showClearButton
                    />

                    <Button
                      type={ButtonType.LINK}
                      onClick={() => setShowSearch(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <button onClick={() => setShowSearch(true)}>
                    <img className="search-icon" src={searchIconImage} />
                  </button>
                )}
              </div>

              <Table
                isLoading={isPending}
                headings={[
                  <div className="px-5 py-[10px] select-none">Name</div>,
                  <div className="px-5 py-[10px] select-none">Type</div>,
                  <div
                    className="px-5 py-[10px] select-none flex gap-[2px] items-center cursor-pointer"
                    onClick={() => {
                      if (sortOptions.sortBy !== SortProperty.LAST_UPDATE) {
                        setSortOptions({
                          sortBy: SortProperty.LAST_UPDATE,
                          sortType: SortType.DESC,
                        });

                        return;
                      }

                      if (sortOptions.sortType === SortType.ASC) {
                        setSortOptions({
                          sortBy: SortProperty.LAST_UPDATE,
                          sortType: SortType.DESC,
                        });

                        return;
                      }

                      setSortOptions({
                        sortBy: SortProperty.LAST_UPDATE,
                        sortType: SortType.ASC,
                      });
                    }}
                  >
                    <div>Last update</div>
                    <div>
                      <img
                        src={
                          sortOptions.sortBy === SortProperty.LAST_UPDATE
                            ? sortOptions.sortType === SortType.ASC
                              ? sortAscChevronsImage
                              : sortDescChevronsImage
                            : sortNoneChevronsImage
                        }
                      />
                    </div>
                  </div>,
                  ,
                  <div className="px-5 py-[10px] select-none"></div>,
                ]}
                rowsData={rows}
                rows={rows.map((row) => [
                  <button
                    className="text-[#6366F1]"
                    onClick={() => navigate(`/templates/${row.type}/${row.id}`)}
                  >
                    {row.name}
                  </button>,
                  <div>{row.type}</div>,
                  <div>
                    {format(new Date(row.lastUpdate), "MM/dd/yyyy HH:mm")}
                  </div>,
                  <Menu as="div" className="relative">
                    <Menu.Button className="px-[5px] py-[11px] rounded">
                      <img
                        className="row-actions-icon"
                        src={threeDotsIconImage}
                      />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute z-[120] right-0 origin-top-right w-[200px] h-[72px] py-[4px] rounded-sm bg-themed-card shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`block w-full text-left py-[5px] px-[12px] ${
                                active ? "bg-themed-background" : ""
                              }`}
                              onClick={() =>
                                handleDuplicateTemplate(row.id.toString())
                              }
                            >
                              Duplicate
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`block w-full text-left py-[5px] px-[12px] text-[#F43F5E] ${
                                active ? "bg-themed-background" : ""
                              }`}
                              onClick={() => handleDeleteTemplate(row.id)}
                            >
                              Delete
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>,
                ])}
              />
            </>
          )}

          {pagesCount > 1 && (
            <div className="flex justify-center items-center">
              <Pagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={pagesCount}
              />
            </div>
          )}
        </div>
      )}

      <CreateTemplateWizardModal
        isOpen={isNameTemplateOpen}
        onClose={() => setIsNameTemplateOpen(false)}
      />

      <TemplateImportModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
      />

      <Modal
        isOpen={isChooserOpen}
        onClose={() => setIsChooserOpen(false)}
        panelClassName="w-[560px] max-w-[95vw] px-[32px] pt-[32px] pb-[24px]"
      >
        <div className="flex flex-col gap-5 font-inter text-[14px] text-themed-text">
          <div className="text-[20px] font-semibold leading-[28px]">
            What do you want to create?
          </div>
          <div className="grid grid-cols-2 gap-4">
            <button
              className="text-left p-4 rounded-md border border-themed-border hover:border-[#6366F1] hover:bg-themed-background transition-colors flex flex-col gap-2"
              onClick={() => {
                setIsChooserOpen(false);
                setIsNameTemplateOpen(true);
              }}
            >
              <div className="font-semibold text-[15px]">Template</div>
              <div className="text-[12px] text-themed-textSecondary">
                A single message (email, SMS, push, etc.) you can drop into a
                workflow.
              </div>
            </button>
            <button
              className="text-left p-4 rounded-md border border-themed-border hover:border-[#6366F1] hover:bg-themed-background transition-colors flex flex-col gap-2"
              onClick={() => {
                setIsChooserOpen(false);
                setActiveTab("sequences");
                setSequenceCreateTrigger((n) => n + 1);
              }}
            >
              <div className="font-semibold text-[15px]">Sequence</div>
              <div className="text-[12px] text-themed-textSecondary">
                A reusable, ordered bundle of templates the AI can drop into
                workflows instead of recreating them.
              </div>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TemplateTablev2;
